import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ScratchPad } from "@/features/reader/notes/ScratchPad";
import { useApi } from "@/hooks/use-api";
import { useAppDispatch, useAppState } from "@/store/useApp";
import { Suggestion } from "@/types/document";
import { Plus } from "lucide-react";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import OnboardingGuide from "../reader/ onboarding/OnboardingGuide";
import OnboardingRubricSuggestionHintFlow from "../reader/ onboarding/flows/OnboardingRubricSuggestionHintFlow";
import { isInlineSuggestion } from "../reader/document/blockUtils";
import { NoteEditorDialog } from "../reader/notes/NoteEditorDialog";
import { BlockReferencesPanel } from "../reader/side-panel/BlockReferencesPanel";
import { RubricBoardItem } from "./RubricBoardItem";

import { config } from "@/config";
import { useFlow } from "@frigade/react";
import OnboardingRubricAnnouncementFlow from "../reader/ onboarding/flows/OnboardingRubricAnnouncementFlow";

export const RubricBoard = () => {
  const currentDocument = useAppState((state) => state.currentDocument);
  const currentRubric = useAppState((state) => state.currentRubric);
  const { approveSuggestion, rejectSuggestion, updateBlock } = useApi();
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>(
    {}
  );
  const [editSuggestion, setEditSuggestion] = useState<Suggestion | null>(null);
  const contentPanelVisible = useAppState(
    (state) => state.referencePanel.visible
  );
  const [selectedSuggestion, setSelectedSuggestion] =
    useState<Suggestion | null>(null);
  const dispatch = useAppDispatch();
  const { flow: suggestionFlow } = useFlow(
    config.frigade.flows.rubricSuggestionHint
  );
  const { flow: viweHint } = useFlow(config.frigade.flows.rubricViewHint);

  // Refs for scroll management
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const criteriaRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const suggestionRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    if (viweHint && !viweHint.isCompleted) {
      viweHint.complete();
    }
  }, [viweHint]);

  // Auto-scroll to keep selected suggestion visible (both horizontally and vertically)
  useEffect(() => {
    if (
      !selectedSuggestion?.id ||
      !selectedSuggestion?.rubricCriteriaId ||
      !scrollContainerRef.current
    ) {
      return;
    }

    const criteriaElement =
      criteriaRefs.current[selectedSuggestion.rubricCriteriaId];
    const suggestionElement = suggestionRefs.current[selectedSuggestion.id];
    if (!criteriaElement || !suggestionElement) {
      return;
    }

    const container = scrollContainerRef.current;
    const containerRect = container.getBoundingClientRect();
    const criteriaRect = criteriaElement.getBoundingClientRect();

    // Check if the criteria column is fully visible horizontally
    const isFullyVisibleHorizontally =
      criteriaRect.left >= containerRect.left &&
      criteriaRect.right <= containerRect.right;

    if (!isFullyVisibleHorizontally) {
      // Calculate scroll position to center the column horizontally
      const scrollLeft =
        criteriaElement.offsetLeft -
        container.offsetWidth / 2 +
        criteriaElement.offsetWidth / 2;

      container.scrollTo({
        left: scrollLeft,
        behavior: "smooth",
      });
    }

    // Scroll the suggestion card into view vertically
    suggestionElement.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "nearest",
    });
  }, [selectedSuggestion]);

  // Sort function for suggestions
  const sortSuggestions = useCallback((suggestions: Suggestion[]) => {
    return [...suggestions].sort((a, b) => {
      if (a.sentiment === "positive" && b.sentiment !== "positive") return -1;
      if (a.sentiment !== "positive" && b.sentiment === "positive") return 1;
      return 0;
    });
  }, []);

  // Group suggestions by rubric criteria
  const suggestionsByCriteria = useMemo(() => {
    if (!currentDocument || !currentRubric) return {};
    return currentRubric.criteria.reduce((acc, criteria) => {
      const filteredSuggestions = currentDocument.suggestions.filter(
        (suggestion) =>
          suggestion.rubricCriteriaId === criteria.id &&
          suggestion.status !== "rejected" // Filter out rejected suggestions
      );
      acc[criteria.id] = sortSuggestions(filteredSuggestions);
      return acc;
    }, {} as Record<string, Suggestion[]>);
  }, [currentDocument, currentRubric, sortSuggestions]);

  const dismissOnboarding = useCallback(() => {
    if (suggestionFlow && !suggestionFlow.isCompleted) {
      suggestionFlow.complete();
    }
  }, [suggestionFlow]);

  const handleApproveSuggestion = useCallback(
    async (suggestion: Suggestion) => {
      if (!suggestion.id || !currentDocument) return;

      setLoadingStates((prev) => ({ ...prev, [suggestion.id!]: true }));
      try {
        await approveSuggestion(currentDocument.id, suggestion);
        if (isInlineSuggestion(suggestion)) {
          const blockId = suggestion.primaryBlockId ?? suggestion.blocks[0];
          if (blockId) {
            await updateBlock(currentDocument.id, blockId, {
              sentiment: suggestion.sentiment,
            });
          }
        }
      } finally {
        setLoadingStates((prev) => ({ ...prev, [suggestion.id!]: false }));
        dismissOnboarding();
      }
    },
    [currentDocument, approveSuggestion, updateBlock, dismissOnboarding]
  );

  const handleRejectSuggestion = useCallback(
    async (suggestion: Suggestion) => {
      if (!suggestion.id || !currentDocument) return;

      setLoadingStates((prev) => ({ ...prev, [suggestion.id!]: true }));
      try {
        await rejectSuggestion(currentDocument.id, suggestion);
        if (isInlineSuggestion(suggestion)) {
          const blockId = suggestion.primaryBlockId ?? suggestion.blocks[0];
          if (blockId) {
            await updateBlock(currentDocument.id, blockId, {
              sentiment: null,
            });
          }
        }
        if (selectedSuggestion?.id === suggestion.id) {
          setSelectedSuggestion(null);
        }
      } finally {
        setLoadingStates((prev) => ({ ...prev, [suggestion.id!]: false }));
        dismissOnboarding();
      }
    },
    [
      currentDocument,
      rejectSuggestion,
      updateBlock,
      selectedSuggestion,
      setSelectedSuggestion,
      dismissOnboarding,
    ]
  );

  // Keyboard navigation handlers
  const navigateSuggestion = useCallback(
    (direction: "up" | "down" | "left" | "right") => {
      if (!currentDocument || !currentRubric) return;

      if (!selectedSuggestion?.id || !selectedSuggestion.rubricCriteriaId) {
        // If no suggestion is selected, select the first one in the first criteria
        const firstCriteria = currentRubric.criteria[0];
        if (firstCriteria) {
          const firstSuggestions =
            suggestionsByCriteria[firstCriteria.id] || [];
          if (firstSuggestions.length > 0) {
            setSelectedSuggestion(firstSuggestions[0]);
          }
        }
        return;
      }

      const currentCriteriaId = selectedSuggestion.rubricCriteriaId;
      const currentCriteriaIndex = currentRubric.criteria.findIndex(
        (c) => c.id === currentCriteriaId
      );

      if (currentCriteriaIndex === -1) return;

      const currentSuggestions = suggestionsByCriteria[currentCriteriaId] || [];
      const currentIndex = currentSuggestions.findIndex(
        (s) => s.id === selectedSuggestion.id
      );

      if (currentIndex === -1) return;

      if (direction === "up") {
        // Navigate to previous card in current criteria
        if (currentIndex > 0) {
          setSelectedSuggestion(currentSuggestions[currentIndex - 1]);
        } else {
          // If on first card, navigate to last card in previous criteria
          if (currentCriteriaIndex > 0) {
            const prevCriteria =
              currentRubric.criteria[currentCriteriaIndex - 1];
            const prevSuggestions =
              suggestionsByCriteria[prevCriteria.id] || [];
            if (prevSuggestions.length > 0) {
              setSelectedSuggestion(
                prevSuggestions[prevSuggestions.length - 1]
              );
            }
          }
        }
      } else if (direction === "down") {
        // Navigate to next card in current criteria
        if (currentIndex < currentSuggestions.length - 1) {
          setSelectedSuggestion(currentSuggestions[currentIndex + 1]);
        } else {
          // If on last card, navigate to first card in next criteria
          if (currentCriteriaIndex < currentRubric.criteria.length - 1) {
            const nextCriteria =
              currentRubric.criteria[currentCriteriaIndex + 1];
            const nextSuggestions =
              suggestionsByCriteria[nextCriteria.id] || [];
            if (nextSuggestions.length > 0) {
              setSelectedSuggestion(nextSuggestions[0]);
            }
          }
        }
      } else if (direction === "right") {
        // Navigate to card with same index in next non-empty criteria, or last card if fewer cards
        // Skip empty columns
        let nextCriteriaIndex = currentCriteriaIndex + 1;
        while (nextCriteriaIndex < currentRubric.criteria.length) {
          const nextCriteria = currentRubric.criteria[nextCriteriaIndex];
          const nextSuggestions = suggestionsByCriteria[nextCriteria.id] || [];
          if (nextSuggestions.length > 0) {
            const targetIndex = Math.min(
              currentIndex,
              nextSuggestions.length - 1
            );
            setSelectedSuggestion(nextSuggestions[targetIndex]);
            break;
          }
          nextCriteriaIndex++;
        }
      } else if (direction === "left") {
        // Navigate to card with same index in previous non-empty criteria, or last card if fewer cards
        // Skip empty columns
        let prevCriteriaIndex = currentCriteriaIndex - 1;
        while (prevCriteriaIndex >= 0) {
          const prevCriteria = currentRubric.criteria[prevCriteriaIndex];
          const prevSuggestions = suggestionsByCriteria[prevCriteria.id] || [];
          if (prevSuggestions.length > 0) {
            const targetIndex = Math.min(
              currentIndex,
              prevSuggestions.length - 1
            );
            setSelectedSuggestion(prevSuggestions[targetIndex]);
            break;
          }
          prevCriteriaIndex--;
        }
      }
    },
    [
      currentDocument,
      currentRubric,
      selectedSuggestion,
      suggestionsByCriteria,
      setSelectedSuggestion,
    ]
  );

  useHotkeys(
    "bracketright",
    () => {
      dispatch({
        type: "SHOW_REFERENCE_PANEL",
        payload: { visible: true, view: "references" },
      });
    },
    {
      preventDefault: true,
    }
  );

  useHotkeys(
    "arrowup",
    () => {
      navigateSuggestion("up");
    },
    {
      preventDefault: true,
      enableOnFormTags: false,
    }
  );

  useHotkeys(
    "arrowdown",
    () => {
      navigateSuggestion("down");
    },
    {
      preventDefault: true,
      enableOnFormTags: false,
    }
  );

  useHotkeys(
    "arrowright",
    () => {
      navigateSuggestion("right");
    },
    {
      preventDefault: true,
      enableOnFormTags: false,
    }
  );

  useHotkeys(
    "arrowleft",
    () => {
      navigateSuggestion("left");
    },
    {
      preventDefault: true,
      enableOnFormTags: false,
    }
  );

  // Approve suggestion with Tab
  useHotkeys(
    "tab",
    (e) => {
      if (selectedSuggestion && selectedSuggestion.status === "pending") {
        e.preventDefault();
        handleApproveSuggestion(selectedSuggestion);
      }
    },
    {
      enableOnFormTags: false,
    }
  );

  // Reject suggestion with Backspace
  useHotkeys(
    "backspace",
    (e) => {
      if (
        selectedSuggestion &&
        (selectedSuggestion.status === "pending" ||
          selectedSuggestion.status === "approved")
      ) {
        e.preventDefault();
        handleRejectSuggestion(selectedSuggestion);
      }
    },
    {
      enableOnFormTags: false,
    }
  );

  if (!currentDocument || !currentRubric) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 w-full">
        No rubric available for this document
      </div>
    );
  }

  const handleSelectSuggestion = (suggestion: Suggestion) => {
    if (selectedSuggestion?.id !== suggestion.id) {
      setSelectedSuggestion(suggestion);
    }
  };

  const handleViewBlocks = (suggestion: Suggestion) => {
    // If the same suggestion is clicked and panel is visible, hide the panel
    if (selectedSuggestion?.id === suggestion.id && contentPanelVisible) {
      dispatch({
        type: "SHOW_REFERENCE_PANEL",
        payload: { visible: false, view: "references" },
      });
    } else {
      // Otherwise, show the panel and set the selected suggestion
      setSelectedSuggestion(suggestion);
      dispatch({
        type: "SHOW_REFERENCE_PANEL",
        payload: { visible: true, view: "references" },
      });
    }
  };

  return (
    <div className="h-full overflow-auto w-full mr-4 mt-6">
      <div className="pl-15 pr-10 pb-10 mb-10">
        <OnboardingRubricAnnouncementFlow />

        <div
          ref={scrollContainerRef}
          className="flex gap-10 overflow-x-auto pb-10"
        >
          {currentRubric.criteria.map((criteria, index) => {
            const suggestions = suggestionsByCriteria[criteria.id] || [];

            return (
              <div
                key={criteria.id}
                ref={(el) => {
                  criteriaRefs.current[criteria.id] = el;
                }}
                className="shrink-0 w-80"
              >
                <div className="flex items-center justify-between mb-1">
                  <h3 className="font-medium text-sm text-muted-foreground">
                    {criteria.criteria}
                  </h3>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => {
                      setEditSuggestion({
                        blocks: [],
                        text: "",
                        sentiment: "neutral",
                        status: "pending",
                        rubricCriteriaId: criteria.id,
                      });
                    }}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>

                <Separator className="mb-4" />

                <div className="space-y-5">
                  {suggestions.map((suggestion) => (
                    <div
                      key={suggestion.id}
                      ref={(el) => {
                        if (suggestion.id) {
                          suggestionRefs.current[suggestion.id] = el;
                        }
                      }}
                    >
                      <RubricBoardItem
                        hint={index === 0}
                        suggestion={suggestion}
                        isLoading={loadingStates[suggestion.id!] || false}
                        isSelected={selectedSuggestion?.id === suggestion.id}
                        isReferencePanelVisible={contentPanelVisible}
                        onApprove={handleApproveSuggestion}
                        onReject={handleRejectSuggestion}
                        onSelect={handleSelectSuggestion}
                        onViewBlocks={handleViewBlocks}
                        onEdit={setEditSuggestion}
                      />
                    </div>
                  ))}

                  {suggestions.length === 0 && (
                    <div className="text-center py-8 text-gray-400 border-2 border-dashed border-gray-200 rounded-lg">
                      No suggestions for this criteria
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {editSuggestion && (
        <NoteEditorDialog
          isOpen={true}
          onClose={() => {
            setEditSuggestion(null);
          }}
          suggestion={editSuggestion}
          sentiment={editSuggestion.sentiment}
        />
      )}

      <BlockReferencesPanel
        isOpen={contentPanelVisible}
        document={currentDocument}
        suggestion={selectedSuggestion}
        placeholder="This is a high-level remark that does not quote any Blocks directly. For more information, view our help center."
      />

      <OnboardingRubricSuggestionHintFlow />
      <OnboardingGuide />

      <ScratchPad />
    </div>
  );
};
